import { ReactComponent as BoltIconSvg } from "app/assets/images/icons/bolt-pointy-solid.svg";
import { LoadingIcon } from "components/LoadingIcon";
import { PeriodComparisonMessage } from "components/PeriodComparisonMessage";
import { TotalValueComparisonChart } from "components/TotalValueComparisonChart";
import { isBefore } from "date-fns";
import { Subheading } from "elements/Subheading";
import { TotalWithUnit } from "elements/TotalWithUnit";
import { expireTime } from "queries/queries.utils";
import { useGetAggregateRatesForTotalUsageQuery } from "queries/ratingApi";
import React from "react";
import { useAppSelector } from "reduxUtils/hook";
import {
  AVAILABLE_PERIODS,
  DisplayUnit,
  UNIT_CODE_TYPES,
} from "utils/constants";
import { jwtClient } from "utils/jwtClient";

const calulatedPreviousBillPeriod = (bill: Bill) => {
  return {
    startAt: bill ? new Date(bill.comparison_data.previous.started_at) : null,
    endAt: bill ? new Date(bill.comparison_data.previous.ended_at) : null,
  };
};

const calulatedYearAgoBillPeriod = (bill: Bill) => {
  return {
    startAt: bill
      ? new Date(bill.comparison_data.one_year_ago.started_at)
      : null,
    endAt: bill ? new Date(bill.comparison_data.one_year_ago.ended_at) : null,
  };
};

type BillUsageSectionProps = {
  bill?: Bill | null;
};

/**
 * @description Used in Bill Page to display usage data of the individual bill as well as comparison usage data for the previous and year ago bill periods.
 */
export const BillUsageSection: React.FC<BillUsageSectionProps> = ({ bill }) => {
  const { supplyNodeRef } = useAppSelector((store) => store.currentAccount);
  const { billingEntityData } = useAppSelector(
    (store) => store.billingEntityData,
  );

  const thisPeriodUsageTotal = bill?.usage?.value;
  const previousBillPeriod = calulatedPreviousBillPeriod(bill);
  const yearAgoBillPeriod = calulatedYearAgoBillPeriod(bill);
  const { periodName = "period" } =
    AVAILABLE_PERIODS[bill?.billing_period?.duration] ?? {};

  const {
    data: previousPeriodUsageTotal,
    isFetching: isPreviousUsageFetching,
  } = useGetAggregateRatesForTotalUsageQuery(
    {
      supplyNodeRef,
      startAt: previousBillPeriod.startAt,
      endAt: previousBillPeriod.endAt,
      jwtClient,
      source: "Individual Bill - Previous Bill Period",
    },
    {
      skip:
        !supplyNodeRef ||
        !previousBillPeriod.startAt ||
        !previousBillPeriod.endAt ||
        !billingEntityData ||
        // query start date must not before user's start date else it will invalid request and expire token
        isBefore(
          previousBillPeriod.startAt,
          new Date(billingEntityData?.start_at),
        ),
      refetchOnMountOrArgChange: expireTime,
    },
  );

  const { data: yearAgoUsageTotal, isFetching: isYearAgoUsageFetching } =
    useGetAggregateRatesForTotalUsageQuery(
      {
        supplyNodeRef,
        startAt: yearAgoBillPeriod.startAt,
        endAt: yearAgoBillPeriod.endAt,
        jwtClient,
        source: "Individual Bill - Year Ago Bill Period",
      },
      {
        skip:
          !supplyNodeRef ||
          !yearAgoBillPeriod.startAt ||
          !yearAgoBillPeriod.endAt ||
          !billingEntityData ||
          // query start date must not before user's start date else it will invalid request and expire token
          isBefore(
            yearAgoBillPeriod.startAt,
            new Date(billingEntityData?.start_at),
          ),
        refetchOnMountOrArgChange: expireTime,
      },
    );

  return (
    <section className="bill-usage-section">
      {thisPeriodUsageTotal ? (
        <>
          <Subheading title="Total used" />
          <TotalWithUnit
            total={thisPeriodUsageTotal}
            icon={<BoltIconSvg data-testid="icon" />}
            unitCode={DisplayUnit.kWh}
          />
        </>
      ) : (
        <Subheading title={`Total used this ${periodName} is not available`} />
      )}
      {isPreviousUsageFetching ? (
        <LoadingIcon />
      ) : (
        <PeriodComparisonMessage
          displayUnit={DisplayUnit.percentage}
          currentPeriod={Number(thisPeriodUsageTotal)}
          lastPeriod={Number(previousPeriodUsageTotal)}
          periodLabel={`last ${periodName}`}
        />
      )}
      {isPreviousUsageFetching || isYearAgoUsageFetching ? (
        <LoadingIcon />
      ) : (
        <TotalValueComparisonChart
          periodName={periodName}
          unitType={UNIT_CODE_TYPES.usage}
          previousValue={Number(previousPeriodUsageTotal)}
          comparisonValue={Number(yearAgoUsageTotal)}
        />
      )}
    </section>
  );
};
